import { createFeatureSelector, createSelector, MemoizedSelector, select, Store } from '@ngrx/store';
import { take } from 'rxjs';
import {BaseAction} from '../store.actions';
import * as _ from 'lodash';
import * as rootReducers from '../store.reducers';
import {DynacrudApi, OperationType, SearchRequest} from '../../core/models/dynacrud';
import {RouteConf} from '../../core/models/route-config';
import {StatusCount} from '../../features/shop/models/entities';
import {logFilterDiag} from '../../shared/utils/filter-leak-diag';

export const selectConf = createFeatureSelector<State, DynaConf>('conf');

export const selectRouteConf: MemoizedSelector<State, RouteConf> = createSelector(selectConf, (state: DynaConf) => state?.route);
export const selectFeatureCode: MemoizedSelector<State, string | undefined> = createSelector(selectRouteConf, (state: RouteConf) => state?.code);
export const selectFeatureTranslationKey: MemoizedSelector<State, string | undefined> = createSelector(selectRouteConf, (state: RouteConf) => state?.translationKey ?? state?.code);

export const selectCurrentDynaData: MemoizedSelector<State, CurrentDynaData> = createSelector(selectConf, (state: DynaConf) => {
  if (state && state.route && state.route.code) {
    const feature: CurrentDynaData = _.cloneDeep(state.dynaData[state.route.code]) as CurrentDynaData;
    feature.route = state.route;
    // Carried onto the slot's own shape: `Refresh` is global (it names no route), and this is the
    // only channel a routed list reads.
    feature.refreshToken = state.refreshToken;
    return feature;

  } else {
    const elseFesture: CurrentDynaData = {
      route: state.route,
      request: null as any,
      data: null as any,
      routeData: null as any
    };
    return elseFesture;
  }
});

/**
 * One selector instance per code, cached. Two reasons it must be cached rather than built per
 * call: a fresh instance memoizes nothing (every subscriber re-runs the `cloneDeep` on every
 * store emission), and `MockStore.overrideSelector` pins an INSTANCE — with a per-call factory a
 * spec could never pin what its component subscribed to.
 */
const slotSelectorsByCode = new Map<string, MemoizedSelector<State, FeatureData | undefined>>();

/**
 * An empty code is never a slot: `dynaData['']` does not exist and never will, so the subscriber
 * silently reads `undefined` for the life of the page. That is what a component gets when it
 * captures its owner code before the route conf reached the store — the failure mode the
 * capture-time warning in {@link captureOwnerCode} exists to name. Warn here as well: a consumer
 * can also be handed an empty code from a `@Input`, a param, or a stale field.
 *
 * Counted, not latched to one. Every empty code is the SAME key, so the message cannot name its
 * call site; a single warning is therefore spent by whichever consumer happens to run first, and
 * a genuinely broken one that mounts later says nothing at all. (That is exactly what happened:
 * `shop-header` held a dead `selectSlotRequest('')` subscription and ate the one warning.) A
 * small budget keeps the console bounded — a subscription in a list re-rendered per row would
 * otherwise flood it — while still surfacing more than one offender.
 */
const EMPTY_SLOT_CODE_WARN_BUDGET = 5;
let emptySlotCodeWarnings = 0;

/**
 * Called at the ENTRY of every PUBLIC slot-selector factory, not inside the cache-miss branch: the
 * caches are module state, so the second consumer of an empty code is a cache HIT and would be
 * warned about nothing at all — including the one whose bug this is.
 */
function warnEmptySlotCode(code: string): void {
  if (code || emptySlotCodeWarnings >= EMPTY_SLOT_CODE_WARN_BUDGET) {
    return;
  }
  emptySlotCodeWarnings++;
  console.warn('[dynaconf] slot selector built for an EMPTY code — it can only ever emit undefined. ' +
    'Capture the owner code with captureOwnerCode(store) once the route conf is in the store.',
    { occurrence: emptySlotCodeWarnings, budget: EMPTY_SLOT_CODE_WARN_BUDGET });
}

/**
 * The cache lookup WITHOUT the warning — what the composite factories below build on.
 *
 * They are public entry points in their own right and each warns for its own caller, so routing
 * them through `selectDynaDataForCode` would charge one offender two of the five warnings and
 * halve how many distinct call sites the budget can surface.
 */
function slotSelectorFor(code: string): MemoizedSelector<State, FeatureData | undefined> {
  let selector = slotSelectorsByCode.get(code);
  if (!selector) {
    selector = createSelector(selectConf, (state: DynaConf) =>
      state?.dynaData?.[code] ? (_.cloneDeep(state.dynaData[code]) as FeatureData) : undefined);
    slotSelectorsByCode.set(code, selector);
  }
  return selector;
}

export const selectDynaDataForCode = (code: string): MemoizedSelector<State, FeatureData | undefined> => {
  warnEmptySlotCode(code);
  return slotSelectorFor(code);
};

/** Spec hook: the warn budget above is module state and would leak across specs. */
export const resetEmptySlotCodeWarning = (): void => {
  emptySlotCodeWarnings = 0;
};

/**
 * The request half of a NAMED slot — the replacement for `selectDynaCrudRequest`, which reads
 * whichever route is active right now. A component that drives an HTTP call, a bulk action, or
 * its own form off the active-route slot picks up a stranger's state during a navigation; read
 * your OWN code instead and that whole failure mode is gone.
 */
const slotRequestSelectorsByCode = new Map<string, MemoizedSelector<State, DynaCrudRequest | undefined>>();

export const selectSlotRequest = (code: string): MemoizedSelector<State, DynaCrudRequest | undefined> => {
  warnEmptySlotCode(code);
  let selector = slotRequestSelectorsByCode.get(code);
  if (!selector) {
    selector = createSelector(slotSelectorFor(code), slot => slot?.request as DynaCrudRequest | undefined);
    slotRequestSelectorsByCode.set(code, selector);
  }
  return selector;
};

/** The items half of a NAMED slot (same rationale as {@link selectSlotRequest}). */
const slotItemsSelectorsByCode = new Map<string, MemoizedSelector<State, any>>();

export const selectSlotItems = (code: string): MemoizedSelector<State, any> => {
  warnEmptySlotCode(code);
  let selector = slotItemsSelectorsByCode.get(code);
  if (!selector) {
    selector = createSelector(slotSelectorFor(code), slot => slot?.routeData?.items);
    slotItemsSelectorsByCode.set(code, selector);
  }
  return selector;
};

/** The selection half of a NAMED slot (same rationale as {@link selectSlotRequest}). */
const slotSelectionSelectorsByCode = new Map<string, MemoizedSelector<State, any>>();

export const selectSlotSelection = (code: string): MemoizedSelector<State, any> => {
  warnEmptySlotCode(code);
  let selector = slotSelectionSelectorsByCode.get(code);
  if (!selector) {
    selector = createSelector(slotSelectorFor(code), slot => slot?.routeData?.selection);
    slotSelectionSelectorsByCode.set(code, selector);
  }
  return selector;
};

/**
 * Spec hook: drop every pinned result the per-code selectors are holding.
 *
 * `MockStore.overrideSelector` pins the value on the SELECTOR INSTANCE (`selector.setResult`),
 * and `MockStore.resetSelectors()` only walks the map of the MockStore that did the overriding —
 * which is constructed empty for every TestBed. Before the per-code caches existed, each spec got
 * a fresh selector instance and the pin died with it; now the instances are module singletons, so
 * a `store.overrideSelector(selectSlotRequest('stock'), …)` in one suite keeps answering for the
 * next suite that reads `'stock'`, and the suite order decides whether the run is green. Called
 * from a root-level `afterEach` (`slot-selector-hygiene.spec.ts`) so no individual spec has to
 * remember. Clears the RESULTS, not the cache: the identity has to survive, or a spec that took
 * its selector reference before the reset and a component that asks for it after would end up on
 * two different instances.
 */
export const resetSlotSelectorResults = (): void => {
  const caches: Map<string, MemoizedSelector<State, any>>[] =
    [slotSelectorsByCode, slotRequestSelectorsByCode, slotItemsSelectorsByCode, slotSelectionSelectorsByCode];
  caches
    .forEach(cache => cache.forEach(selector => {
      selector.release();
      selector.clearResult();
    }));
};

/**
 * Capture the route code a component OWNS, once, at construction — the first half of the
 * ownership rule (`ownerCodeOf` in this file is the other half). Every consumer used to inline
 * `store.pipe(select(selectFeatureCode), take(1)).subscribe(c => this.ownerCode = c ?? '')`, and
 * the `?? ''` in ~25 copies was a silent failure: an empty code makes every later read
 * (`dynaData['']`) undefined forever AND every later dispatch dropped, with nothing said. One
 * helper so the empty case is reported once, in one place.
 *
 * Synchronous on purpose: an NgRx selector emits the current state on subscribe, so the value is
 * assigned before this returns. It is only ever empty when the route conf has not reached the
 * store yet — a component built outside a `RouteConf.position` slot, or a spec with no
 * `selectFeatureCode` override.
 *
 * Budgeted like the other two warnings here: this runs at CONSTRUCTION, and a component
 * constructed per table row (a column or an action) would otherwise log once per row, on every
 * page of every search.
 */
const CAPTURE_WARN_BUDGET = 5;
let captureWarnings = 0;

export function captureOwnerCode(store: Store<State>): string {
  let code = '';
  store.pipe(select(selectFeatureCode), take(1)).subscribe(c => code = c ?? '');
  if (!code && captureWarnings < CAPTURE_WARN_BUDGET) {
    captureWarnings++;
    console.warn('[dynaconf] captureOwnerCode(): no active route code. Every slot read will be ' +
      'undefined and every slot write will be dropped. Capture the code after the route conf is ' +
      'dispatched, or pass the owning code explicitly.',
      { occurrence: captureWarnings, budget: CAPTURE_WARN_BUDGET });
  }
  return code;
}

/** Spec hook: the budget above is module state and would leak across specs. */
export const resetCaptureOwnerCodeWarnings = (): void => {
  captureWarnings = 0;
};

// Stable, shared instance of the session_detail slot selector. Exposing a single
// named instance (instead of calling the selectDynaDataForCode factory inline)
// lets consumers and their specs reference the SAME selector, so a test can pin it
// with MockStore.overrideSelector — which the per-call factory instance can't.
export const selectSessionDetailSlot: MemoizedSelector<State, FeatureData | undefined> =
  selectDynaDataForCode('session_detail');

// Stable, shared instance of the reports slot selector (same rationale as
// selectSessionDetailSlot). The shop reports page reads its OWN slot by fixed code
// rather than the active-route slot (selectDynaCrudRequest), so its /v2/orderDetails/summary
// side-fetch can never pick up a foreign feature's filter during a fast navigation away
// (the read-side leak that hit /v2/orderDetails/summary with stock/dashboard filters).
export const selectReportsSlot: MemoizedSelector<State, FeatureData | undefined> =
  selectDynaDataForCode('reports');

// Stable, shared instance of the stock slot selector (same rationale as selectReportsSlot). The
// stock list reads its OWN slot by fixed code for the /v2/stocks/summary side-fetch, so a fast
// navigation away can never drive it with a foreign feature's filter.
export const selectStockSlot: MemoizedSelector<State, FeatureData | undefined> =
  selectDynaDataForCode('stock');

export const selectCurrentDynaDataOffSessions: MemoizedSelector<State, CurrentDynaData> = createSelector(selectConf, (state: DynaConf) => {
  if (state && state.route && state.route.code) {
    // const feature: CurrentDynaData = _.cloneDeep(state.dynaData['session']) as CurrentDynaData;
    const featureWrappsessions =  _.cloneDeep(state.dynaData['sessions_wrappers']) as CurrentDynaData;
    featureWrappsessions.route = state.route;
    // if ( featureWrappsessions.routeData && featureWrappsessions.routeData.selection ) {

    //   (feature.routeData.selection as {all: boolean, rows: {selected: boolean, row: any}[] }).rows.push(...featureWrappsessions.routeData.selection.rows);
    // }
    return featureWrappsessions;

  } else {
    const elseFesture: CurrentDynaData = {
      route: state.route,
      request: null as any,
      data: null as any,
      routeData: null as any
    };
    return elseFesture;
  }
});


export const selectDynaCrudRequest: MemoizedSelector<State, DynaCrudRequest | undefined> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => state.request);

export const selectDynaCrudData: MemoizedSelector<State, DynaCrudRequest | undefined> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => state.data);

export const selectRouteData: MemoizedSelector<State, DynaCrudData | undefined> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => state.routeData);

export const selectCurrentSelection: MemoizedSelector<State, any> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => {
  return state?.routeData?.selection;
});

export const selectCurrentSelectionOffSessions: MemoizedSelector<State, any> = createSelector(selectCurrentDynaDataOffSessions, (state: CurrentDynaData) => {
  return state?.routeData?.selection;
});


export const selectCurrentItems: MemoizedSelector<State, any> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => {
  return state?.routeData?.items;
});
export const selectCurrentRow: MemoizedSelector<State, any> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => {
  return state?.routeData?.rowSelect;
});

export const selectReloadPage: MemoizedSelector<State, any> = createSelector(selectCurrentDynaData, (state: CurrentDynaData) => {
  return state?.routeData?.resetPage;
});


export interface DynaConf {
  route: RouteConf;
  dynaData: DynaData;
  /**
   * Bumped by `Refresh`, and the only thing that action leaves behind.
   *
   * `Refresh` used to return `{...state}` and change NOTHING a consumer could name, so a list
   * re-searched on it purely as a side effect: `selectCurrentDynaData` deep-CLONES its slot, so any
   * new root object made it emit, and `DefaultListComponent`'s "nothing changed" clause then
   * happened to fire. That worked until a genuine reason to ignore an emission arrived (a row
   * highlight, which must not reload a list that keeps state on its rows) -- at which point the
   * refresh after a successful delete disappeared with it, because both dispatches land in the same
   * tick and the subscription's debounce collapses them into one emission. An intent that is
   * invisible in the state cannot be told apart from the write it happens to travel with.
   */
  refreshToken?: number;
}

export interface RouteData {
  selection?: any;
  selectionItem?: any;
  customData?: any;
  items?: StatusCount[];
  rowSelect?: RowSelect;
  resetPage?: boolean;
  warehouses?: any | any[];
}

export interface RowSelect {
  select: boolean;
  row: any;

  [key: string]: any;
}

export interface FeatureData {
  request?: DynaCrudRequest;
  data?: DynaCrudData;
  routeData?: RouteData;
}

export interface DynaData {
  [featureCode: string]: FeatureData;
}

export interface CurrentDynaData extends FeatureData {
  route: RouteConf;
  /** Carried through from {@link DynaConf.refreshToken} so a consumer can see an explicit reload. */
  refreshToken?: number;
}

export interface State extends rootReducers.State {
  conf: DynaConf;
}

export interface DynaCrudRequest {
  customData?: any;
  searchRequest?: SearchRequest;
}

export interface DynaCrudRequestOnlyWithCode {
  customData?: any;
  searchRequest?: SearchRequest;
  code: string
}

export interface DynaCrudData {
  response?: DynacrudApi<any>;
  customData?: any;
}

const initialState: DynaConf = {route: null as any, dynaData: {}};

export enum DynaConfActionType {
  ConfChange = '[DynaConf] ConfChange',
  FilterChange = '[DynaConf] FilterChange',
  FilterWithCode = '[DynaConf] FilterWithCode',
  PaginationSortChange = '[DynaConf] PaginationSortChange',
  SelectionChange = '[DynaConf] SelectionChange',
  SelectionItem = '[DynaConf] SelectionItem',
  // DeletedItems = '[DynaConf] DeletedItems',
  Reset = '[DynaConf] Reset',
  Refresh = '[DynaConf] Refresh',
  SearchSuccess = '[DynaConf] SearchSuccess',
  ChangeItems = '[DynaConf] ChangeItems',
  RowSelect = '[DynaConf] RowSelect',
  ResetPage = '[DynaConf] ResetPage',
  /*
    WarehousesSelect = '[DynaConf] WarehousesSelect',
    Add =  '[Dynacrud] Add',
    AddSuccess =  '[Dynacrud] Add',
    Edit =  '[Dynacrud] Edit',
    EditSuccess =  '[Dynacrud] EditSuccess',
    Remove = '[Dynacrud] Remove',
    RemoveSuccess = '[Dynacrud] RemoveSuccess',
    HasChanges = '[Dynacrud] HasChanges',

    // FIXME a che serve il singolo get?
    Load =  '[Dynacrud] Load',
    LoadSuccess =  '[Dynacrud] LoadSuccess',
  */
}

const defualtDynaCrudRequest: DynaCrudRequest = {searchRequest: {filter: {}}};

// DEBUG: slots to watch for unintended filter/sort mutations. `stock` is the
// source slot for the stock-shaped filter+sort; the others are the leak targets.
// `cart` is watched too: it is a stock-shaped list whose slot was poisoned with an
// orders sort (`priority`), and the ring was blind to it while the list held only the
// movements-leak slots.
const WATCHED_SLOTS = ['session_detail', 'session', 'stock', 'cart'];

// Snapshot BOTH filter and sort: the original leak diagnostic watched `filter`
// only and was therefore blind to the sort leak (`order by actualPrice`), which
// is what actually breaks the /sessions/{id}/movements query.
function snapshotWatchedSlots(state: DynaConf): Record<string, any> {
  const snap: Record<string, any> = {};
  for (const slot of WATCHED_SLOTS) {
    const searchRequest = state?.dynaData?.[slot]?.request?.searchRequest;
    snap[slot] = { filter: searchRequest?.filter, sort: searchRequest?.sort };
  }
  return snap;
}

/**
 * THE OWNERSHIP RULE. Every action that writes a `dynaData` slot must name the slot it owns —
 * as `action.ownerCode` (bare payloads) or `payload.code` (envelope payloads). Resolving the slot
 * from the ACTIVE route instead is what the whole cross-route leak class is made of: dispatches
 * are routinely deferred (async EventEmitters, debounces, `setTimeout`, `take(1)` after a
 * navigation), so "the route that is active when this reduces" is not the route that dispatched,
 * and the write lands in a stranger's slot — persisted, so it survives every reload.
 *
 * `payload.code` is honoured for the THREE envelope actions only (`FilterChange`,
 * `PaginationSortChange`, `FilterWithCode`), whose payload is a request wrapper that has always
 * carried the owning code. Everything else passes `BaseAction`'s third argument, because their
 * payloads are domain objects — a row, a selection, an item list — and `code` is an ordinary
 * field on those (a product code, a customer code, a status code). Reading one as a route code
 * does not drop the write, it writes a slot named after a customer: worse than the anonymous
 * write this rule exists to refuse, and invisible.
 */
type OwnerSource = 'owner-arg' | 'envelope';

function ownerCodeOf(action: BaseAction, source: OwnerSource): string | undefined {
  if (action.ownerCode) {
    return action.ownerCode;
  }
  if (source !== 'envelope') {
    return undefined;
  }
  const payload = action.payload;
  return payload && typeof payload === 'object' ? (payload as { code?: string }).code : undefined;
}

/**
 * A dropped write is a bug at the dispatch site, not a user-visible failure — say so loudly, but
 * BUDGETED, and per action type.
 *
 * The offending dispatcher is usually one that fires on every interaction (a table status
 * emission, a filter keystroke), so an unbudgeted warn does not report a bug once — it reports it
 * on every click for the life of the page, burying whatever else the console had to say. Per
 * action type rather than one global budget so a chatty `PaginationSortChange` cannot silence a
 * `RowSelect` that is broken for a different reason.
 */
const UNOWNED_WARN_BUDGET = 5;
const unownedWarnings = new Map<string, number>();

function warnUnowned(actionType: string, state: DynaConf, reason = 'no owner code'): void {
  const spent = unownedWarnings.get(actionType) ?? 0;
  if (spent >= UNOWNED_WARN_BUDGET) {
    return;
  }
  unownedWarnings.set(actionType, spent + 1);
  console.warn(`[${actionType}] DROPPED (${reason})`,
    { currentCode: state.route?.code, occurrence: spent + 1, budget: UNOWNED_WARN_BUDGET });
}

/** Spec hook: the budget above is module state and would leak across specs. */
export const resetUnownedWarnings = (): void => {
  unownedWarnings.clear();
};

function dynaConfReducerInner(state: DynaConf, action: BaseAction): DynaConf {
  switch (action.type) {

    case DynaConfActionType.Reset: {
      const newState = {...initialState};
      return newState;
    }

    case DynaConfActionType.RowSelect: {
      const rowSelectCode = ownerCodeOf(action, 'owner-arg');
      if (!rowSelectCode) {
        warnUnowned(action.type, state);
        return state;
      }
      const newState = {...state};
      newState.dynaData[rowSelectCode] = newState.dynaData[rowSelectCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[rowSelectCode];
      feature.routeData = feature.routeData || {};
      feature.routeData.rowSelect = action.payload;
      return newState;

    }
    case DynaConfActionType.ResetPage: {
      const resetPageCode = ownerCodeOf(action, 'owner-arg');
      if (!resetPageCode) {
        warnUnowned(action.type, state);
        return state;
      }
      const newState = {...state};
      newState.dynaData[resetPageCode] = newState.dynaData[resetPageCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[resetPageCode];
      feature.routeData = feature.routeData || {};
      (feature.routeData.resetPage as boolean) = action.payload;
      if (feature.request && feature.routeData.resetPage) {
        feature.request.customData = _.pick(feature.request.customData, ['brand', 'category', 'categorys', 'selectedStatus']);
      }
      return newState;
    }

    case DynaConfActionType.Refresh: {
      const newState = {...state};
      // See DynaConf.refreshToken: the bump IS the action's payload. Without it `Refresh` changes
      // nothing observable and a list can only re-search by accident.
      newState.refreshToken = (state.refreshToken ?? 0) + 1;
      return newState;
    }
    case DynaConfActionType.ConfChange: {
      const newState = {...state};
      newState.route = action.payload;
      if (newState.route) {
        newState.dynaData[newState.route.code as string] = newState.dynaData[newState.route.code as string] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      }

      return newState;
    }
    case DynaConfActionType.PaginationSortChange: {
      // The ownership rule (see `ownerCodeOf`), and the failure that made it one: table status
      // emissions are deferred twice (`status` is an async EventEmitter, `paginator.page` is
      // debounced), so a sort/paginate on list A reduced after a navigation used to land A's
      // pagination+sort in B's slot. That is how `order by priority` (an orders column) reached
      // the cart slot and 500'd every later /v2/stocks call — permanently, since the slot is
      // persisted and FilterChange never rewrites `sort`.
      const payloadCode = ownerCodeOf(action, 'envelope');
      if (!payloadCode) {
        warnUnowned(action.type, state);
        return state;
      }

      const newState = {...state};
      newState.dynaData[payloadCode] = newState.dynaData[payloadCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[payloadCode];
      const searchRequest = (feature.request as DynaCrudRequest).searchRequest = feature.request?.searchRequest || {};

      searchRequest.pagination = action.payload.pagination;
      searchRequest.sort = action.payload.reset ? searchRequest.sort : action.payload.sort;

      return newState;
    }
    case DynaConfActionType.SelectionChange: {
      const selectionCode = ownerCodeOf(action, 'owner-arg');
      if (!selectionCode) {
        warnUnowned(action.type, state);
        return state;
      }
      const newState = {...state};
      newState.dynaData[selectionCode] = newState.dynaData[selectionCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[selectionCode];
      feature.routeData = feature.routeData || {};
      feature.routeData.selection = action.payload ? action.payload : {all: false, rows: []};
      return newState;
    }

    // NO DISPATCHER REMAINS for SelectionItem (the last one went with the transfer-type rework);
    // the case is kept, guarded like the rest, because the action type is still exported and a
    // feature re-adopting it must land on the ownership rule rather than on the ambient slot.
    // Delete the case and the action together if it is ever retired for good.
    case DynaConfActionType.SelectionItem: { // fixme santiago MI SERVE PER TUTTE DUE MODI --> TRANSFER TYPE "TRANSFER" e TYPE "UNLOAD" ??
      const selectionItemCode = ownerCodeOf(action, 'owner-arg');
      if (!selectionItemCode) {
        warnUnowned(action.type, state);
        return state;
      }
      const newState = {...state};
      newState.dynaData[selectionItemCode] = newState.dynaData[selectionItemCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[selectionItemCode];
      feature.routeData = feature.routeData || {};


      if (feature.routeData.selection.rows.length >= 1) {
        feature.routeData.selection.rows.filter((item: {
          row: { id: any; };
        }) => item.row.id !== action.payload.row.id);
        feature.routeData.selection.rows.push(action.payload.row.id ? action.payload : {selected: false, row: {}});
        feature.routeData.selection.rows = _.uniqWith(feature.routeData.selection.rows, _.isEqual); // necessariamente block tutto quello che arriva ?
      } else {
        feature.routeData.selection.rows.push(action.payload.row.id ? action.payload : {selected: false, row: {}});
        feature.routeData.selection.rows = _.uniqWith(feature.routeData.selection.rows, _.isEqual); // necessariamente block tutto quello che arriva ?

      }

      return newState;
    }

    case DynaConfActionType.ChangeItems: {
      const changeItemsCode = ownerCodeOf(action, 'owner-arg');
      if (!changeItemsCode) {
        warnUnowned(action.type, state);
        return state;
      }
      const newState = {...state};
      newState.dynaData[changeItemsCode] = newState.dynaData[changeItemsCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[changeItemsCode];
      feature.routeData = feature.routeData || {};
      feature.routeData.items = action.payload;
      return newState;
    }

    case DynaConfActionType.FilterWithCode: {
      const code = ownerCodeOf(action, 'envelope');
      // Named in the action's own name, but nothing enforced it: a missing code used to create a
      // slot literally keyed `"undefined"`, which every later reader then missed.
      if (!code) {
        warnUnowned(action.type, state);
        return state;
      }

      const newState = {...state};
      newState.dynaData[code as string] = newState.dynaData[code as string] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[code as string];

      if (feature && feature.request) {
        feature.request.customData = action.payload.customData ? _.cloneDeep(action.payload.customData) : null;
        if (feature.request.searchRequest) {
          feature.request.searchRequest.filter = action.payload.searchRequest ? _.cloneDeep(action.payload.searchRequest.filter) : null;

          if (feature.request.searchRequest.pagination && parseInt(feature.request.searchRequest.pagination.length as string) > 20 &&
          (parseInt(feature.request.searchRequest.pagination.page as string) > 0) && !action.payload.searchRequest.pagination?.active) {

            feature.request.searchRequest.pagination = {...feature.request.searchRequest.pagination};

          } else {

            // feature.request.searchRequest.pagination = {
            //   ...action.payload.searchRequest.pagination || {
            //     page: 0,
            //     size: 20
            //   }
            // };

            feature.request.searchRequest.pagination = {
              page: action.payload.searchRequest.pagination?.page != undefined ?  action.payload.searchRequest.pagination?.page : feature.request.searchRequest.pagination?.page || 0,
              size:  feature.request.searchRequest.pagination?.size || action.payload.searchRequest.pagination?.size || 20,
            }

          }

        }
      }


      return newState;

    }

    case DynaConfActionType.FilterChange: {
      const currentCode = state.route?.code as string | undefined;
      const payloadCode = ownerCodeOf(action, 'envelope');
      // A FilterChange dispatch must carry the owning component's route code,
      // and it must match the currently active route. This rejects late-firing
      // dispatches from a destroyed/navigated-away component, which would
      // otherwise leak that feature's filter into the new route's data slot.
      //
      // WHY THIS ONE IS STRICTER THAN THE REST. Every other slot write now accepts an off-route
      // owner: naming your own slot IS the proof of ownership, and a component that legitimately
      // writes its own sort/selection after a navigation (a dialog, a 500ms setTimeout) must be
      // able to. A filter cannot use that licence, because it is the only write that decides what
      // the list SEARCHES FOR: `default-list` re-runs its query off this slot, so a filter applied
      // to a list the user has left changes the rows waiting for them on their return, with no
      // gesture of theirs behind it. The active-route match keeps a filter tied to the screen the
      // user is looking at. An off-route feature that genuinely needs to seed another list's
      // filter has `FilterWithCode`, which is owner-only by design (it is how the
      // status-throughput drill-down hands its filter to the orders list).
      if (!currentCode || !payloadCode || payloadCode !== currentCode) {
        // Budgeted like every other drop in this reducer: the dispatcher is usually a debounced
        // filter that fires on every keystroke, so an unbudgeted warn buries the console.
        warnUnowned(action.type, state,
          payloadCode ? `owner ${payloadCode} is not the active route` : 'no owner code');
        return state;
      }
      const newState = {...state};
      newState.dynaData[currentCode] = newState.dynaData[currentCode] || {request: _.cloneDeep(defualtDynaCrudRequest)};
      const feature: FeatureData = newState.dynaData[currentCode];

      if (feature && feature.request) {
        feature.request.customData = action.payload.customData ? _.cloneDeep(action.payload.customData) : null;
        if (feature.request.searchRequest) {
          const clonedFilter = action.payload.searchRequest ? _.cloneDeep(action.payload.searchRequest.filter) : null;
          // DIAGNOSTIC (filter-leak): stamp the dispatching component into the
          // filter so it rides with the filter even when it leaks into another
          // slot. The backend logs the raw searchRequest on error, so `_src`
          // surfaces in BE error logs and names the source. Backend ignores
          // unknown JSON properties, so this is inert to query processing.
          const srcTag = (action.payload as any)?.srcTag;
          if (clonedFilter && srcTag) {
            (clonedFilter as any)._src = srcTag;
          }
          feature.request.searchRequest.filter = clonedFilter;

          feature.request.searchRequest.pagination = {
            page: action.payload.searchRequest.pagination?.page != undefined ?  action.payload.searchRequest.pagination?.page : feature.request.searchRequest.pagination?.page || 0,
            size:  feature.request.searchRequest.pagination?.size || action.payload.searchRequest.pagination?.size || 20,
          }

        }
      }


      return newState;
    }
    default:
      return state;
  }

}

export function dynaConfReducer(state: DynaConf = initialState, action: BaseAction): DynaConf {
  const before = snapshotWatchedSlots(state);
  const next = dynaConfReducerInner(state, action);
  const after = snapshotWatchedSlots(next);
  for (const slot of WATCHED_SLOTS) {
    if (!_.isEqual(before[slot], after[slot])) {
      // Persist only structural metadata — never the raw action payload
      // (can be large, may contain user-entered text). `srcTag` names the
      // dispatching component (set by the stock-shape filter builders); the
      // trimmed stack is a fallback when the dispatcher carries no tag.
      let stack: string | undefined;
      try {
        stack = (new Error().stack || '').split('\n').slice(2, 8).join(' | ');
      } catch {
        stack = undefined;
      }
      logFilterDiag('dynaData.mutated', {
        slot,
        actionType: action.type,
        payloadCode: (action.payload as any)?.code,
        srcTag: (action.payload as any)?.srcTag,
        currentRouteCode: next.route?.code,
        beforeFilter: before[slot]?.filter,
        afterFilter: after[slot]?.filter,
        beforeSort: before[slot]?.sort,
        afterSort: after[slot]?.sort,
        stack,
      });
    }
  }
  return next;
}


